import { SocialLogin } from "@capgo/capacitor-social-login";
import Axios from "axios";
import { GET_SETTINGS_URL } from "../configs";
import { isNativePush, nativePlatform } from "./nativePush";

/**
 * Google sign-in for the website and the Capacitor apps. Returns the Google ID token, which the server
 * verifies (see POST /api/social-login). The client IDs come from admin Settings -> Social Login via
 * /get-settings (localStorage), so enabling/changing them needs no app release.
 */

const read = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
};

export const isGoogleLoginAvailable = () => read("enableGoogleLogin") === "true" && !!read("googleAppId");

/**
 * The provider flags live in localStorage, which only /get-settings fills. Screens that do not load
 * settings themselves (the delivery login on a fresh install) or a tap before that request finished would
 * wrongly read "Google is off". Fetch them once when they are missing.
 */
export const ensureSocialSettings = async () => {
  if (read("googleAppId") && read("enableGoogleLogin")) return;
  try {
    const response = await Axios.post(GET_SETTINGS_URL, null, { timeout: 8000 });
    const settings = response && response.data;
    // The API answers with a list of { key, value } rows (the redux store writes them the same way).
    if (Array.isArray(settings)) {
      settings.forEach((row) => {
        if (row && row.key && row.value !== null && row.value !== undefined) localStorage.setItem(row.key, row.value);
      });
    }
  } catch (e) {
    /* offline: the availability check below shows the usual message */
  }
};

/** Apple sign-in runs natively on iPhone/iPad only for now (web/Android need an Apple Services ID + callback). */
export const isAppleLoginAvailable = () =>
  read("enableAppleLogin") === "true" && isNativePush() && nativePlatform() === "ios";

/** Shown when a button is tapped but its provider is switched off / not set up yet. */
export const providerUnavailableMessage = (provider) =>
  provider === "apple"
    ? "Sign in with Apple is not available yet. Please use your phone number."
    : "Google sign-in is not available right now. Please use your phone number.";

let initializedWith = null;

const isCancellation = (error) => {
  const text = `${(error && error.code) || ""} ${(error && error.message) || error || ""}`.toLowerCase();
  return /cancel|dismiss|closed|aborted|user_cancelled|popup_closed/.test(text);
};

/**
 * @returns {Promise<{ idToken: string, name: string|null } | { cancelled: true }>}
 * @throws Error with a user-safe `.message` for real failures
 */
export const signInWithGoogle = async () => {
  const webClientId = read("googleAppId");
  if (!webClientId) throw new Error("Google sign-in is not available right now.");
  const iOSClientId = read("googleIosClientId");

  try {
    const signature = `${webClientId}|${iOSClientId}`;
    if (initializedWith !== signature) {
      await SocialLogin.initialize({
        google: {
          webClientId,
          iOSClientId: iOSClientId || undefined,
          iOSServerClientId: webClientId,
          mode: "online",
        },
      });
      initializedWith = signature;
    }

    const response = await SocialLogin.login({
      provider: "google",
      options: {
        // No `scopes`: email/profile/openid are the plugin defaults, and on Android any custom scope makes the
        // plugin reject unless MainActivity is modified ("You CANNOT use scopes without modifying the main activity").
        // Show every account on the device (also supervised accounts), not only previously used ones.
        filterByAuthorizedAccounts: false,
        forceRefreshToken: false,
      },
    });

    const result = response && response.result;
    const idToken = result && result.idToken;
    if (!idToken) throw new Error("Google did not return a sign-in token. Please try again.");
    const profile = (result && result.profile) || {};
    return { idToken, name: profile.name || null };
  } catch (error) {
    if (isCancellation(error)) return { cancelled: true };
    console.warn("[socialAuth] google sign-in failed", isNativePush() ? "native" : "web", error);
    throw new Error("Google sign-in failed. Please try again or use your phone number.");
  }
};

/**
 * Sign in with Apple (iPhone/iPad app). Apple only returns the name on the FIRST authorisation, so it is
 * passed along with the token.
 * @returns {Promise<{ idToken: string, name: string|null } | { cancelled: true }>}
 */
export const signInWithApple = async () => {
  if (!isAppleLoginAvailable()) throw new Error(providerUnavailableMessage("apple"));
  try {
    await SocialLogin.initialize({ apple: {} });
    const response = await SocialLogin.login({
      provider: "apple",
      options: { scopes: ["email", "name"] },
    });
    const result = response && response.result;
    const idToken = result && result.idToken;
    if (!idToken) throw new Error("Apple did not return a sign-in token. Please try again.");
    const profile = (result && result.profile) || {};
    const name = [profile.givenName, profile.familyName].filter(Boolean).join(" ").trim();
    return { idToken, name: name || null };
  } catch (error) {
    if (isCancellation(error)) return { cancelled: true };
    console.warn("[socialAuth] apple sign-in failed", error);
    throw new Error("Apple sign-in failed. Please try again or use your phone number.");
  }
};

// ---- Web: full-page redirect (no pop-up, so it is never blocked and works on mobile browsers) ----

const WEB_STATE_KEY = "googleWebSignInState";

const randomString = () => {
  const bytes = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

const decodeJwtPayload = (jwt) => {
  try {
    const part = jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(window.atob(part))));
  } catch (e) {
    return {};
  }
};

/** Where Google sends the browser back to. Must be listed under "Authorized redirect URIs" of the Web client. */
export const googleWebRedirectUri = () => `${window.location.origin}/login`;

const buildGoogleAuthUrl = (state, nonce) =>
  `https://accounts.google.com/o/oauth2/v2/auth?${new URLSearchParams({
    client_id: read("googleAppId"),
    redirect_uri: googleWebRedirectUri(),
    response_type: "id_token",
    scope: "openid email profile",
    nonce,
    state,
    prompt: "select_account",
  }).toString()}`;

const saveWebState = (state, nonce) => {
  try {
    sessionStorage.setItem(WEB_STATE_KEY, JSON.stringify({ state, nonce }));
  } catch (e) {
    throw new Error("Please enable cookies/storage to sign in with Google.");
  }
};

/** Google refuses to render its sign-in page inside a frame (e.g. a browser "device simulator"). */
const isEmbeddedInFrame = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

/**
 * Validates Google's answer (the URL hash) against the state/nonce saved when sign-in started.
 * @returns {null | {cancelled:true} | {error:string} | {idToken:string,name:string|null}}
 */
const parseGoogleHash = (rawHash) => {
  const hash = rawHash ? rawHash.replace(/^#/, "") : "";
  if (!hash || !/(^|&)(id_token|error)=/.test(hash)) return null;
  const params = new URLSearchParams(hash);

  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(WEB_STATE_KEY) || "null");
  } catch (e) {
    saved = null;
  }
  if (!saved || params.get("state") !== saved.state) return null; // not ours / replayed
  try {
    sessionStorage.removeItem(WEB_STATE_KEY);
  } catch (e) {
    /* ignore */
  }

  if (params.get("error")) {
    return /access_denied|cancel/i.test(params.get("error")) ? { cancelled: true } : { error: "Google sign-in failed. Please try again." };
  }
  const idToken = params.get("id_token");
  const claims = idToken ? decodeJwtPayload(idToken) : {};
  if (!idToken || claims.nonce !== saved.nonce) {
    return { error: "Google sign-in could not be verified. Please try again." };
  }
  return { idToken, name: claims.name || null };
};

const POPUP_MESSAGE = "zaaapit-google-signin";

/**
 * Starts Google sign-in on the website. MUST be called straight from the tap handler (no await before it)
 * so the pop-up is not blocked.
 *  - normal tab: leaves the page for Google, returns { redirecting: true }; the answer is read by
 *    consumeGoogleRedirectResult() when Google sends the browser back.
 *  - inside a frame: opens a pop-up window and returns { promise } resolving to the parsed result.
 */
export const startGoogleWebSignIn = () => {
  const state = randomString();
  const nonce = randomString();
  saveWebState(state, nonce);
  const url = buildGoogleAuthUrl(state, nonce);

  if (!isEmbeddedInFrame()) {
    window.location.assign(url);
    return { redirecting: true };
  }

  const width = 500;
  const height = 650;
  const left = Math.max(0, window.screenX + (window.outerWidth - width) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - height) / 2);
  const popup = window.open(url, "zaaapit-google-signin", `width=${width},height=${height},left=${left},top=${top}`);
  if (!popup) throw new Error("Please allow pop-ups for this site to sign in with Google.");

  const promise = new Promise((resolve) => {
    let closedTimer;
    const finish = (value) => {
      window.removeEventListener("message", onMessage);
      clearInterval(closedTimer);
      resolve(value);
    };
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || !event.data || event.data.type !== POPUP_MESSAGE) return;
      finish(parseGoogleHash(event.data.hash) || { error: "Google sign-in could not be verified. Please try again." });
    };
    window.addEventListener("message", onMessage);
    closedTimer = setInterval(() => {
      try {
        if (popup.closed) finish({ cancelled: true });
      } catch (e) {
        /* cross-origin while on Google's page */
      }
    }, 600);
  });
  return { promise };
};

/**
 * Runs when the app boots inside the sign-in pop-up (Google sent it back to /login#...): hands the answer
 * to the window that opened it and closes. No-op in every other page.
 */
export const relayGooglePopupResultIfNeeded = () => {
  try {
    if (isNativePush() || !window.opener || window.opener === window) return;
    const hash = window.location.hash || "";
    if (!/(^|[#&])state=/.test(hash) || !/(^|[#&])(id_token|error)=/.test(hash)) return;
    window.opener.postMessage({ type: POPUP_MESSAGE, hash }, window.location.origin);
    window.close();
  } catch (e) {
    /* not in our pop-up */
  }
};

/**
 * Call on the login screen after load. If the URL carries Google's answer (full-page redirect flow),
 * validates it and returns { idToken, name } / { cancelled: true } / { error }; null when there is none.
 */
export const consumeGoogleRedirectResult = () => {
  const result = parseGoogleHash(window.location.hash);
  if (result) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  return result;
};
